import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, MessageSquare, Phone, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { DirectMessageDialog } from "@/components/DirectMessageDialog";
import { SprintInvitationAcceptDialog } from "@/components/sprint/SprintInvitationAcceptDialog";
import { CallInvitationAcceptDialog } from "@/components/call/CallInvitationAcceptDialog";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string | null;
  action_url: string | null;
  read: boolean;
  created_at: string;
  metadata?: any;
}

export const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [sentNotifications, setSentNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dmDialogOpen, setDmDialogOpen] = useState(false);
  const [dmRecipientId, setDmRecipientId] = useState<string | undefined>();
  
  // Sprint invitation dialog state
  const [sprintInvitationDialogOpen, setSprintInvitationDialogOpen] = useState(false);
  const [selectedSprintInvitation, setSelectedSprintInvitation] = useState<{
    notificationId: string;
    sprintId: string;
    sprintObjective: string;
    inviterName: string;
    customMessage?: string;
    invitationId: string;
  } | null>(null);

  // Call invitation dialog state
  const [callInvitationDialogOpen, setCallInvitationDialogOpen] = useState(false);
  const [selectedCallInvitation, setSelectedCallInvitation] = useState<{
    notificationId: string;
    callId: string;
    callTitle: string;
    callDescription?: string;
    inviterName: string;
    bountyAmount?: number;
    callType?: string;
  } | null>(null);

  useEffect(() => {
    if (!user) return;

    loadNotifications();

    // Subscribe to real-time notifications
    const receivedChannel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          loadNotifications();
        }
      )
      .subscribe();

    const sentChannel = supabase
      .channel('sent-notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sent_notifications',
          filter: `sender_id=eq.${user.id}`
        },
        () => {
          loadNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(receivedChannel);
      supabase.removeChannel(sentChannel);
    };
  }, [user]);

  const loadNotifications = async () => {
    if (!user) return;

    // Load received notifications
    const { data: receivedData } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);

    if (receivedData) {
      setNotifications(receivedData);
      setUnreadCount(receivedData.filter(n => !n.read).length);
    }

    // Load sent notifications
    const { data: sentData } = await supabase
      .from('sent_notifications')
      .select('*')
      .eq('sender_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);

    if (sentData) {
      setSentNotifications(sentData);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Handle sprint invitation with interactive response
    if (notification.type === 'sprint_invitation' && notification.metadata?.requires_response) {
      setSelectedSprintInvitation({
        notificationId: notification.id,
        sprintId: notification.metadata.sprint_id,
        sprintObjective: notification.metadata.sprint_objective,
        inviterName: notification.metadata.inviter_name,
        customMessage: notification.metadata.custom_message,
        invitationId: notification.metadata.invitation_id,
      });
      setSprintInvitationDialogOpen(true);
      return;
    }

    // Handle call invitation with interactive response
    if (notification.type === 'call_invitation' && notification.metadata?.requires_response) {
      setSelectedCallInvitation({
        notificationId: notification.id,
        callId: notification.metadata.call_id,
        callTitle: notification.metadata.call_title,
        callDescription: notification.metadata.call_description,
        inviterName: notification.metadata.inviter_name,
        bountyAmount: notification.metadata.bounty_amount,
        callType: notification.metadata.call_type,
      });
      setCallInvitationDialogOpen(true);
      return;
    }

    // Mark as read
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notification.id);

    // Handle direct message notifications by opening DM dialog
    if (notification.type === 'direct_message') {
      const metadata = notification.metadata as any;
      if (metadata?.sender_id) {
        setDmRecipientId(metadata.sender_id);
        setDmDialogOpen(true);
      }
    } else if (notification.action_url) {
      // Navigate to action URL for other notification types
      navigate(notification.action_url);
    }

    loadNotifications();
  };

  const markAllAsRead = async () => {
    if (!user) return;

    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false);

    loadNotifications();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'direct_message':
        return <MessageSquare className="h-4 w-4 mr-2 flex-shrink-0" />;
      case 'call':
      case 'call_invitation':
        return <Phone className="h-4 w-4 mr-2 flex-shrink-0" />;
      case 'sprint_invitation':
        return <Users className="h-4 w-4 mr-2 flex-shrink-0" />;
      default:
        return <Bell className="h-4 w-4 mr-2 flex-shrink-0" />;
    }
  };

  if (!user) return null;

  return (
    <TooltipProvider>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="active" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                  >
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Notifications</p>
          </TooltipContent>
        </Tooltip>
      <DropdownMenuContent align="end" className="w-[600px]">
        <div className="flex items-center justify-between p-2 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="h-auto p-1 text-xs"
            >
              Mark all read
            </Button>
          )}
        </div>
        <div className="flex h-[400px]">
          {/* Left half - Received */}
          <div className="w-1/2 border-r">
            <h4 className="text-xs font-semibold p-2 border-b bg-muted">Received</h4>
            <ScrollArea className="h-[360px]">
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No received notifications
                </div>
              ) : (
                notifications.map((notification) => (
                  <HoverCard key={notification.id} openDelay={300}>
                    <HoverCardTrigger asChild>
                      <DropdownMenuItem
                        className={`p-3 cursor-pointer ${!notification.read ? 'bg-muted/50' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start w-full">
                          {getNotificationIcon(notification.type)}
                          <div className="space-y-1 flex-1">
                            <p className={`text-sm ${!notification.read ? 'font-bold text-green-600' : 'font-medium text-foreground'}`}>
                              {notification.title}
                            </p>
                            {notification.message && (
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {notification.message}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              {new Date(notification.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    </HoverCardTrigger>
                    <HoverCardContent side="left" align="start" className="w-80 z-[10000]">
                      <div className="space-y-2">
                        <p className="font-semibold text-sm">{notification.title}</p>
                        {notification.message && (
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                ))
              )}
            </ScrollArea>
          </div>
          
          {/* Right half - Sent */}
          <div className="w-1/2">
            <h4 className="text-xs font-semibold p-2 border-b bg-muted">Sent</h4>
            <ScrollArea className="h-[360px]">
              {sentNotifications.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No sent notifications
                </div>
              ) : (
                sentNotifications.map((notification) => (
                  <HoverCard key={notification.id} openDelay={300}>
                    <HoverCardTrigger asChild>
                      <DropdownMenuItem className="p-3">
                        <div className="flex items-start w-full">
                          {getNotificationIcon(notification.type)}
                          <div className="space-y-1 flex-1">
                            <p className="text-sm font-medium text-foreground">{notification.title}</p>
                            {notification.message && (
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {notification.message}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              {new Date(notification.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    </HoverCardTrigger>
                    <HoverCardContent side="right" align="start" className="w-80 z-[10000]" sideOffset={5}>
                      <div className="space-y-2">
                        <p className="font-semibold text-sm">{notification.title}</p>
                        {notification.message && (
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                ))
              )}
            </ScrollArea>
          </div>
        </div>
      </DropdownMenuContent>
      </DropdownMenu>
      
      <DirectMessageDialog
        open={dmDialogOpen} 
        onOpenChange={setDmDialogOpen} 
        preselectedRecipientId={dmRecipientId} 
      />
      
      {selectedSprintInvitation && (
        <SprintInvitationAcceptDialog
          open={sprintInvitationDialogOpen}
          onOpenChange={(open) => {
            setSprintInvitationDialogOpen(open);
            if (!open) {
              setSelectedSprintInvitation(null);
              loadNotifications();
            }
          }}
          notificationId={selectedSprintInvitation.notificationId}
          sprintId={selectedSprintInvitation.sprintId}
          sprintObjective={selectedSprintInvitation.sprintObjective}
          inviterName={selectedSprintInvitation.inviterName}
          customMessage={selectedSprintInvitation.customMessage}
          invitationId={selectedSprintInvitation.invitationId}
        />
      )}

      {selectedCallInvitation && (
        <CallInvitationAcceptDialog
          open={callInvitationDialogOpen}
          onOpenChange={(open) => {
            setCallInvitationDialogOpen(open);
            if (!open) {
              setSelectedCallInvitation(null);
              loadNotifications();
            }
          }}
          notificationId={selectedCallInvitation.notificationId}
          callId={selectedCallInvitation.callId}
          callTitle={selectedCallInvitation.callTitle}
          callDescription={selectedCallInvitation.callDescription}
          inviterName={selectedCallInvitation.inviterName}
          bountyAmount={selectedCallInvitation.bountyAmount}
          callType={selectedCallInvitation.callType}
        />
      )}
    </TooltipProvider>
  );
};